import { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';
const PDF_UPLOAD_MAX_FILES = 1;
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 min, couvre un PDF de ~50 images

function buildUploadFormData(selectedFile, knowledgeBaseId, analyzeImages) {
  const formData = new FormData();
  formData.append('file', selectedFile);
  formData.append('knowledge_base_id', knowledgeBaseId);
  formData.append('analyze_images', analyzeImages);
  return formData;
}

function buildUploadSuccessMessage(responseData) {
  let message = responseData.message;
  if (responseData.images_analyzed > 0) {
    message += ` (${responseData.images_analyzed} images analysées)`;
  }
  return message;
}

function usePdfDropzone(setSelectedFile) {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, [setSelectedFile]);

  return useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: PDF_UPLOAD_MAX_FILES,
    onDrop,
  });
}

async function pollUploadJob(jobId, { onPhase, signal }) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new Error('aborted');
    }
    const { data } = await axios.get(`${API}/documents/upload-job/${jobId}`);
    if (data.phase_label) {
      onPhase(data.phase_label);
    }
    if (data.status === 'completed') {
      return data.result;
    }
    if (data.status === 'error') {
      throw new Error(data.error_message || "Erreur lors de l'upload");
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  throw new Error("Délai d'analyse dépassé (15 min). Consultez les logs backend.");
}

function usePdfUploadHandler({
  selectedFile,
  knowledgeBaseId,
  analyzeImages,
  onSuccess,
  setUploading,
  setPhaseLabel,
}) {
  const abortRef = useRef(null);

  const createUploadData = useCallback(
    () => buildUploadFormData(selectedFile, knowledgeBaseId, analyzeImages),
    [analyzeImages, knowledgeBaseId, selectedFile]
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    setPhaseLabel('Envoi du fichier...');
    abortRef.current = new AbortController();

    try {
      const startResponse = await axios.post(
        `${API}/documents/upload-pdf`,
        createUploadData(),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { job_id: jobId } = startResponse.data;
      if (!jobId) {
        throw new Error('Réponse serveur invalide (job_id manquant)');
      }

      setPhaseLabel('Démarrage de l\'analyse...');
      const result = await pollUploadJob(jobId, {
        onPhase: setPhaseLabel,
        signal: abortRef.current.signal,
      });

      if (result?.duplicate) {
        toast.error(result.message || 'Document déjà présent');
        return;
      }
      toast.success(buildUploadSuccessMessage(result));
      onSuccess();
    } catch (error) {
      const message = error?.response?.data?.detail || error?.message || "Erreur lors de l'upload";
      toast.error(message);
    } finally {
      setUploading(false);
      setPhaseLabel('');
      abortRef.current = null;
    }
  }, [createUploadData, onSuccess, selectedFile, setPhaseLabel, setUploading]);

  return handleUpload;
}

export function usePdfUpload({ knowledgeBaseId, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzeImages, setAnalyzeImages] = useState(true);
  const [phaseLabel, setPhaseLabel] = useState('');

  const dropzone = usePdfDropzone(setSelectedFile);
  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
  }, []);
  const handleUpload = usePdfUploadHandler({
    selectedFile,
    knowledgeBaseId,
    analyzeImages,
    onSuccess,
    setUploading,
    setPhaseLabel,
  });

  return {
    uploading,
    selectedFile,
    analyzeImages,
    setAnalyzeImages,
    dropzone,
    clearSelectedFile,
    handleUpload,
    phaseLabel,
  };
}
